import { useState } from "react";
import {
  useRecordContext,
  ReferenceInput,
  AutocompleteInput,
  ReferenceManyField,
  TextField,
  FunctionField,
  Pagination,
  TextInput,
  DateField,
  NumberField,
  SelectInput,
  BooleanInput,
  BooleanField,
  required,
} from "react-admin";
import { EditableDatagrid } from "@react-admin/ra-editable-datagrid";
import { Link } from "react-router-dom";
import { Box, Typography, Chip } from "@mui/material";
import { GroupShareDefinitionForm } from "./GroupShareDefinitionForm";

const ENTITY_ACCESS_CHOICES = [
  { id: 1, name: "Preview" },
  { id: 2, name: "Preview + Read" },
  { id: 3, name: "Preview + Read + Update" },
];

const ENTITY_RULE_LABELS = {
  investmentsDynamicShare: "Investments",
  firmsDynamicShare: "Firms",
  personsDynamicShare: "Persons",
  documentsDynamicShare: "Documents",
} as const;

function getEntitySlot(def: any) {
  const [ruleType, payload] =
    Object.entries(ENTITY_RULE_LABELS).find(([key]) => def[key]) ?? [null, null];
  return ruleType ? { label: payload, payload: def[ruleType] } : null;
}

function DynamicDefinitionDisplay({ record }: { record: any }) {
  if (!record.dynamicDefinitionStructured) {
    return (
      <Chip
        label="No Rules Defined"
        size="small"
        variant="outlined"
        color="warning"
      />
    );
  }

  const def = record.dynamicDefinitionStructured;
  const entitySlot = getEntitySlot(def);

  if (entitySlot) {
    const setId = entitySlot.payload.investmentSet?.setId;
    const documentTypes: number[] | undefined =
      entitySlot.label === "Documents"
        ? entitySlot.payload.documentTypes
        : undefined;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Chip
          label={entitySlot.label}
          size="small"
          color="primary"
          variant="outlined"
        />
        {setId && (
          <Typography variant="caption" color="text.secondary">
            Set ID: {setId}
          </Typography>
        )}
        {documentTypes && documentTypes.length > 0 && (
          <Chip
            label={`${documentTypes.length} doc type(s)`}
            size="small"
            color="info"
            variant="outlined"
          />
        )}
        {def.description && (
          <Typography variant="caption" color="text.secondary">
            {def.description}
          </Typography>
        )}
      </Box>
    );
  }

  // Fallback for unknown structure
  return (
    <Chip
      label="Dynamic Rules"
      size="small"
      color="secondary"
      variant="outlined"
    />
  );
}
function GroupShareDefinitionEdit() {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <ReferenceManyField
      reference="groupShareDefinition"
      target="sourceGroupId"
      label="Group Share Definitions"
      perPage={25}
    >
      <EditableDatagrid
        resource="groupShareDefinition"
        createForm={<GroupShareDefinitionForm sourceGroupId={record.id} />}
        editForm={<GroupShareDefinitionForm sourceGroupId={record.id} />}
        bulkActionButtons={false}
      >
        <TextField source="id" sortable={false} />

        <FunctionField
          label="Target Group"
          sortable={false}
          render={(record: any) => (
            <Link to={`/user/${record.targetGroupId}`}>
              <Box>
                <Typography variant="body2" component="div">
                  {record.targetGroup?.commonName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {record.targetGroup?.organization?.name || "No Org"} (ID:{" "}
                  {record.targetGroup?.organizationId || "N/A"})
                </Typography>
              </Box>
            </Link>
          )}
        />

        <FunctionField
          label="Access Level"
          sortable={false}
          render={(record: any) => {
            const accessId = record.entityAccessId;
            const accessChoice = ENTITY_ACCESS_CHOICES.find(
              choice => choice.id === accessId
            );
            return accessChoice?.name || `Level ${accessId}`;
          }}
        />

        <BooleanField source="isActive" label="Active" sortable={false} />

        <FunctionField
          label="Rule Configuration"
          sortable={false}
          render={(record: any) => <DynamicDefinitionDisplay record={record} />}
        />

        <DateField source="createDate" label="Created" sortable={false} />
        <DateField
          source="lastExecuted"
          label="Last Executed"
          sortable={false}
        />
      </EditableDatagrid>
      <Pagination />
    </ReferenceManyField>
  );
}

export default GroupShareDefinitionEdit;
